import { useState, ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { TourxLogo } from './TourxLogo';

interface NavItem {
  id: string;
  label: string;
  icon: ReactNode;
  href: string;
  roles?: string[];
  section?: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'home', label: 'Dashboard', href: '/', section: 'main',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />,
  },
  {
    id: 'drivers', label: 'Drivers', href: '/drivers', section: 'compliance',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />,
  },
  {
    id: 'tacho', label: 'Tacho Analysis', href: '/tacho', section: 'compliance',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />,
  },
  {
    id: 'vu-downloads', label: 'VU Downloads', href: '/vu-downloads', section: 'compliance',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />,
  },
  {
    id: 'calendar', label: 'Worker Calendar', href: '/calendar', section: 'compliance',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />,
  },
  {
    id: 'compliance', label: 'Compliance & DCRS', href: '/compliance', section: 'compliance',
    roles: ['admin', 'transport_manager'],
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />,
  },
  {
    id: 'intelligence', label: 'Intelligence', href: '/intelligence', section: 'compliance',
    roles: ['admin', 'transport_manager'],
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />,
  },
  {
    id: 'fleet', label: 'Fleet', href: '/fleet', section: 'operations',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />,
  },
  {
    id: 'tracking', label: 'Vehicle Tracking', href: '/tracking', section: 'operations',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" />,
  },
  {
    id: 'depots', label: 'Depots', href: '/depots', section: 'operations',
    roles: ['admin', 'transport_manager'],
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />,
  },
  {
    id: 'quote', label: 'Quotes', href: '/quote', section: 'commercial',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />,
  },
  {
    id: 'jobs', label: 'Jobs', href: '/jobs', section: 'commercial',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />,
  },
  {
    id: 'accounts', label: 'Accounts', href: '/accounts', section: 'commercial',
    roles: ['admin', 'transport_manager'],
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
  },
  {
    id: 'walkaround', label: 'Walkaround Checks', href: '/walkaround', section: 'operations',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />,
  },
  {
    id: 'workshop', label: 'Workshop', href: '/workshop', section: 'operations',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.42 15.17l-5.65-5.65a4 4 0 010-5.66l.71-.71a4 4 0 015.66 0l5.65 5.65a4 4 0 010 5.66l-.71.71a4 4 0 01-5.66 0z M19.07 4.93a10 10 0 010 14.14M4.93 19.07a10 10 0 010-14.14" />,
  },
  {
    id: 'ops', label: 'Operations', href: '/ops', section: 'operations',
    roles: ['admin', 'transport_manager'],
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />,
  },
  {
    id: 'jobsheets', label: 'Job Sheets', href: '/jobsheets', section: 'operations',
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
  },
  {
    id: 'reports', label: 'Reports', href: '/reports', section: 'admin',
    roles: ['admin', 'transport_manager'],
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />,
  },
  {
    id: 'users', label: 'Users', href: '/users', section: 'admin',
    roles: ['admin', 'transport_manager'],
    icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />,
  },
];

const SECTIONS: { key: string; label: string }[] = [
  { key: 'main', label: '' },
  { key: 'compliance', label: 'COMPLIANCE' },
  { key: 'operations', label: 'OPERATIONS' },
  { key: 'commercial', label: 'COMMERCIAL' },
  { key: 'admin', label: 'ADMINISTRATION' },
];

export function PortalLayout({ children, currentPage }: { children: ReactNode; currentPage: string }) {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Filter by role first, then by module access
  const userModules = (user as any)?.allowed_modules as string[] | null | undefined;
  const visibleItems = NAV_ITEMS.filter(item => {
    // Role check
    if (item.roles && !(user && item.roles.includes(user.role))) return false;
    // Module access check — null/undefined means full access
    if (userModules && userModules.length > 0) {
      return userModules.includes(item.id);
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-56' : 'w-14'} bg-slate-900 text-white flex flex-col transition-all duration-150 flex-shrink-0`}>
        {/* Logo */}
        <div className="h-12 flex items-center px-3 border-b border-slate-700/50">
          {sidebarOpen ? (
            <TourxLogo height={28} />
          ) : (
            <div className="flex items-center justify-center w-8 h-8 flex-shrink-0">
              <TourxLogo height={18} />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-1 overflow-y-auto">
          {SECTIONS.map(section => {
            const sectionItems = visibleItems.filter(i => (i.section || 'main') === section.key);
            if (sectionItems.length === 0) return null;
            return (
              <div key={section.key}>
                {section.label && sidebarOpen && (
                  <p className="px-3 pt-4 pb-1 text-[10px] font-semibold text-slate-500 tracking-widest uppercase">{section.label}</p>
                )}
                {!section.label && <div className="h-1" />}
                {sectionItems.map((item) => {
                  const active = currentPage === item.id;
                  return (
                    <a
                      key={item.id}
                      href={item.href}
                      className={`flex items-center gap-2.5 px-3 py-2 text-[13px] font-medium transition-colors ${
                        active
                          ? 'bg-slate-700/60 text-white border-l-2 border-blue-400'
                          : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200 border-l-2 border-transparent'
                      }`}
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {item.icon}
                      </svg>
                      {sidebarOpen && <span>{item.label}</span>}
                    </a>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* User section */}
        <div className="border-t border-slate-700/50 p-2">
          {sidebarOpen ? (
            <div className="flex items-center gap-2 px-1">
              <div className="w-7 h-7 bg-slate-600 rounded flex items-center justify-center text-[11px] font-semibold text-slate-200 flex-shrink-0">
                {user?.first_name?.[0]}{user?.last_name?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-medium text-slate-300 truncate">{user?.first_name} {user?.last_name}</p>
                <p className="text-[10px] text-slate-500 truncate">{user?.company_name || user?.role}</p>
              </div>
              <button onClick={logout} className="text-slate-500 hover:text-slate-300 p-1" title="Sign out">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          ) : (
            <button onClick={logout} className="text-slate-500 hover:text-slate-300 mx-auto block p-1" title="Sign out">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          )}
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="border-t border-slate-700/50 py-2 text-slate-500 hover:text-slate-300 text-center"
        >
          <svg className={`w-3.5 h-3.5 mx-auto transition-transform ${sidebarOpen ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        </button>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {children}
      </div>
    </div>
  );
}
